import { BuildStates, FINISHED_STATES } from "~/constants/BuildStates";
import { Step } from "~/lib/pipeline";
import { Job } from "~/components/build/Show/lib/types/Job";
import { FilterByOption } from "~/components/build/Show/components/Sidebar/useFilterStore";

type ClusterQueue = {
  key: string;
  path: string;
  hosted: boolean;
};

export interface Build {
  loading: boolean;
  rebuiltFrom?: {
    url: string;
    number: number;
  };
  triggeredFrom?: {
    uuid: string;
    url: string;
    name: string;
    project?: {
      name: string;
    };
    build?: {
      number: number;
    };
  };
  pipelineSchedule?: {
    url: string;
    label: string;
  };
  pipelineTrigger?: {
    url: string;
    label: string;
  };
  creatorVerified: boolean;
  cancelPath: string;
  canRetryFailedJobs: boolean;
  canRetryFailedTriggeredJobs: boolean;
  retryFailedJobsPath: string;
  nextBranchBuild?: {
    url: string;
    number: number;
    state: BuildStates;
  };
  prevBranchBuild?: {
    url: string;
    number: number;
    state: BuildStates;
  };
  authorName?: string;
  authorAvatar?: string;
  id: string;
  jsonPath: string;
  message?: string;
  number: number;
  account: {
    slug: string;
    name: string;
    jobMinuteLimitReached: boolean;
  };
  project: {
    id: string;
    name: string;
    slug: string;
    description?: string;
    url: string;
    public: boolean;
    archived: boolean;
    provider: {
      id: string;
      url: string;
      frontendIcon: string;
    };
    exampleRepository: boolean;
    emoji?: string;
    color?: string;
    allowRebuilds: boolean;
    permissions: {
      edit?: {
        allowed: boolean;
      };
    };
    bktecBadgeEnabled: boolean;
    hasTestPlans: boolean;
  };
  steps: Step[];
  jobs: Job[];
  state: BuildStates;
  cancelReason?: string;
  blockedState?: string;
  createdAt: string;
  canceledAt?: string;
  cancelledBy?: {
    name: string;
    avatar: string;
  };
  startedAt?: string;
  scheduledAt?: string;
  finishedAt?: string;
  source: string;
  pullRequest?: {
    id: string;
    url: string;
  };
  branchName: string;
  branchPath: string;
  commitId: string;
  commitUrl?: string;
  commitShortLength: number;
  jobsCount: number;
  totalJobDuration: number;
  annotationCountsByStyle: {
    [key: string]: number;
  };
  path: string;
  dispatchPausedClusterQueues: {
    key: string;
    path: string;
    dispatchPausedAt: string;
    dispatchPausedBy: {
      name: string;
      avatar: {
        url: string;
      };
    };
    dispatchPausedNote?: string | null;
  }[];
  duration?: {
    total: number;
    running: number;
    runningRetries: number;
    idle: number;
  };
  retry_counts?: {
    total: number;
    manual: number;
    automatic: number;
  };
  jobsRunning?: boolean;
  jobsIdle?: boolean;
  retriesRunning?: boolean;
  testEngine: {
    testTabUrl?: string;
  };
  permissions: {
    retry: {
      allowed: boolean;
      reason?: string;
      message?: string;
    };
    rebuild: {
      allowed: boolean;
      reason?: string;
      message?: string;
    };
    cancel: {
      allowed: boolean;
      reason?: string;
      message?: string;
    };
  };
  rebuildPath: string;
  rebuildBranchPath: string;
  newPath: string;
  mergeQueueBaseBranch: string | null;
  mergeQueuePath: string;
  buildDataBasePath: string;
  applyFailedFilter?: boolean;
  availableFilters?: {
    states: FilterByOption[];
    runningJobStates: FilterByOption[];
  };
  statistics?: {
    jobsCount: number;
    jobsStartedCount: number;
    jobsFailedCount: number;
    // Step counts
    stepsCount: number;
    stepsFailedCount: number;
    stepsPassedCount: number;
    stepsRunningCount: number;
    stepsScheduledCount: number;
    stepsWaitingCount: number;
    stepsSoftFailedCount: number;
    stepsCanceledCount: number;
    stepsIgnoredCount: number;
  };
  stepUploadsCount: number;
  clusterQueues: ClusterQueue[];
}

export function isBuildFinished(build: { state: BuildStates }) {
  return FINISHED_STATES.has(build.state);
}

export function isBuildFailing(build: { state: BuildStates }) {
  return build.state === "failing";
}
